import { useQuery, useQueryClient } from '@tanstack/react-query';
import { inventoryKeys } from './keys.js';
import { useInventoryClient } from './provider.js';
import { useIdempotentMutation } from './useIdempotentMutation.js';
export function useWarehouses(options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.locations.warehouses(),
        queryFn: ({ signal }) => client.locations.listWarehouses(signal),
        staleTime: 60_000,
        ...options,
    });
}
export function useWarehouse(warehouseId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.locations.warehouse(warehouseId ?? ''),
        queryFn: ({ signal }) => client.locations.getWarehouse(warehouseId, signal),
        enabled: Boolean(warehouseId),
        ...options,
    });
}
/** The default warehouse, or the first one. What a posting form should preselect. */
export function useDefaultWarehouse() {
    const { data } = useWarehouses();
    if (!data || data.length === 0)
        return null;
    return data.find((w) => w.is_default) ?? data[0] ?? null;
}
export function useWarehouseCount(options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.locations.warehouseCount(),
        queryFn: ({ signal }) => client.locations.countWarehouses(signal),
        ...options,
    });
}
export function useZones(warehouseId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.locations.zones(warehouseId ?? ''),
        queryFn: ({ signal }) => client.locations.listZones(warehouseId, signal),
        enabled: Boolean(warehouseId),
        ...options,
    });
}
export function useBins(zoneId, options) {
    const client = useInventoryClient();
    return useQuery({
        queryKey: inventoryKeys.locations.bins(zoneId ?? ''),
        queryFn: ({ signal }) => client.locations.listBins(zoneId, signal),
        enabled: Boolean(zoneId),
        ...options,
    });
}
export function useCreateWarehouse() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.locations.createWarehouse(input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.locations.all() });
        },
    });
}
export function useUpdateWarehouse(warehouseId) {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.locations.updateWarehouse(warehouseId, input, operation),
        onSuccess: () => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.locations.all() });
        },
    });
}
export function useCreateZone() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.locations.createZone(input, operation),
        onSuccess: (zone) => {
            void queryClient.invalidateQueries({
                queryKey: inventoryKeys.locations.zones(zone.warehouse_id),
            });
        },
    });
}
export function useCreateBin() {
    const client = useInventoryClient();
    const queryClient = useQueryClient();
    return useIdempotentMutation({
        mutationFn: (input, operation) => client.locations.createBin(input, operation),
        onSuccess: (bin) => {
            void queryClient.invalidateQueries({ queryKey: inventoryKeys.locations.bins(bin.zone_id) });
        },
    });
}
//# sourceMappingURL=locations.js.map